import { PageHeader } from "@/components/data-display/page-header";
import { StatCardRow } from "@/components/data-display/stat-card";
import { TableState } from "@/components/data-display/table-state";
import { UnavailablePanel } from "@/components/data-display/unavailable-value";
import { WipHint } from "@/components/ui/wip-hint";
import { Table, TableWrapper } from "@/components/ui/table";
import { WipNotice, WipReason, WipPanel } from "@/features/treasury/treasury-wip";

/**
 * LIQ-00 Liquidity Dashboard — designed layout, no backend in this environment
 * (design-port round 3, 2026-10-03).
 *
 * Ported from `.design-ref/prototype/features/treasury/liquidity.tsx`
 * (`LiquidityDashboard`) with its sample venues, quotes and dispositions
 * removed: venue connectivity, balances, exposure and quote data were
 * previously fabricated here (page-delivery-pipeline.md G6, 2026-09-23). The
 * designer's header, stat row and two tables are kept; stat values render as
 * unknown, each table renders its designed empty row, and the page makes no
 * API call (see context/page_pipeline/BACKEND_GAPS.md).
 *
 * Deliberately not ported, both wireframe-only chrome rather than product
 * structure: the designer's state-tab switcher (a "visual toggle only" that
 * swaps between fixture states — there is no state to switch to without a
 * live source) and the disposition legend (it names statuses that exist only
 * in the wireframe's fixtures). Both are recorded in report.md.
 */

const DEFERRED_TITLE = "Liquidity is not available in this environment yet";
const DEFERRED_DESCRIPTION =
  "Venue balances, quotes, and execution monitoring have no backend in this environment yet. This page will show real venue data once the liquidity domain is built.";

const UNAVAILABLE = "Not available yet";

const VENUE_COLUMNS = ["ID", "Venue", "Type", "Status", "Balance", "Action"];
const QUOTE_COLUMNS = ["Time", "Pair", "Action", "Venue", "Price", "Disposition"];

export default function TreasuryLiquidityPage() {
  return (
    <div>
      <PageHeader
        title="Liquidity Dashboard"
        subtitle="Venue connectivity, exposure, unsettled movements, and execution quotes across liquidity venues."
        actions={<WipHint />}
      />
      <WipReason />
      <WipNotice title={DEFERRED_TITLE} description={DEFERRED_DESCRIPTION} />

      <StatCardRow
        className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
        stats={[
          { value: "", label: "Connected Venues", hint: UNAVAILABLE },
          { value: "", label: "Venue Exposure", hint: UNAVAILABLE },
          { value: "", label: "Unsettled Movements", hint: UNAVAILABLE },
          { value: "", label: "Degraded Venues", hint: UNAVAILABLE },
        ]}
      />

      <WipPanel title="Venue Balances" bodyClassName="p-0" className="mb-5">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr>
                {VENUE_COLUMNS.map((column) => (
                  <th key={column} scope="col">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <TableState
                variant="empty"
                colSpan={VENUE_COLUMNS.length}
                title="No venues connected"
                description="Connect an exchange, issuer, or DEX to start trading. Venue readiness is read from the liquidity backend, which does not exist in this environment yet."
              />
            </tbody>
          </Table>
        </TableWrapper>
      </WipPanel>

      <WipPanel title="Recent Quotes" bodyClassName="p-0" className="mb-5">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr>
                {QUOTE_COLUMNS.map((column) => (
                  <th key={column} scope="col">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <TableState
                variant="empty"
                colSpan={QUOTE_COLUMNS.length}
                title="No recent quotes"
                description="Quotes are requested from and recorded by the liquidity backend, which does not exist in this environment yet."
              />
            </tbody>
          </Table>
        </TableWrapper>
      </WipPanel>

      <UnavailablePanel
        title="Disposition Legend"
        detail="Quote and order disposition states are published by the liquidity backend, which does not exist in this environment yet."
      />
    </div>
  );
}
